import { AxiosResponse } from 'axios';
import {
  User,
  UserWithRole,
  Customer,
  UserPreference,
  Role,
  PermissionCatalogGroup,
} from '../../types/mold';
import api, { setAuthToken } from './client';

export const authService = {
  login: async (
    username: string,
    password: string,
  ): Promise<AxiosResponse<{ token: string; userId: number }>> => {
    return api.post('/login', { username, password });
  },
  logout: async (): Promise<void> => {
    // Optional: await api.post('/logout') when the endpoint exists.
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    setAuthToken(null);
  },
};

// User Service
export const userService = {
  getUsers: async (
    withRole: boolean = false,
  ): Promise<AxiosResponse<(User | UserWithRole)[]>> => {
    return api.get('/users', {
      params: { withRole },
    });
  },

  createUser: async (data: {
    username: string;
    password: string;
    roleId?: number;
  }): Promise<AxiosResponse<User>> => {
    return api.post('/users', data);
  },

  updateUser: async (
    id: number,
    data: {
      password?: string;
      isActive?: boolean;
      roleId?: number;
    },
  ): Promise<AxiosResponse<User>> => {
    return api.patch(`/users/${id}`, data);
  },

  getAvatar: async (id: number, version?: number): Promise<AxiosResponse<Blob>> => {
    return api.get(`/users/${id}/avatar`, {
      responseType: 'blob',
      params: version ? { v: version } : undefined,
    });
  },

  uploadAvatar: async (id: number, avatar: File): Promise<void> => {
    const formData = new FormData();
    formData.append('avatar', avatar);
    await api.post(`/users/${id}/avatar`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  deleteAvatar: async (id: number): Promise<void> => {
    await api.delete(`/users/${id}/avatar`);
  },

  getUserPreferences: async (
    id: number,
  ): Promise<AxiosResponse<UserPreference>> => {
    return api.get(`/users/${id}/frontend-preferences`);
  },

  updateUserPreferences: async (
    id: number,
    data: Partial<UserPreference>,
  ): Promise<AxiosResponse<void>> => {
    return api.post(`/users/${id}/frontend-preferences`, data);
  },
};

// Role Service
export const roleService = {
  getRoles: async (): Promise<AxiosResponse<Role[]>> => {
    return api.get('/roles');
  },

  updateRole: async (id: number, data: {}): Promise<AxiosResponse<Role>> => {
    return api.patch(`/roles/${id}`, data);
  },

  // Assignable-permission catalog (grouped by resource) rendered by the editor.
  getPermissionCatalog: async (): Promise<
    AxiosResponse<{ groups: PermissionCatalogGroup[] }>
  > => {
    return api.get('/role-permissions/catalog');
  },

  // Replace a role's entire permission set with `permissions` (names). Returns
  // the refreshed role.
  updateRolePermissions: async (
    id: number,
    permissions: string[],
  ): Promise<AxiosResponse<Role>> => {
    return api.put(`/role-permissions/${id}`, { permissions });
  },
};

export const customerService = {
  getCustomers: async (): Promise<AxiosResponse<Customer[]>> => {
    return api.get('/customers');
  },
  getOptions: async (): Promise<{ value: number; label: string }[]> => {
    const response = await customerService.getCustomers();
    return response.data.map((customer) => ({
      value: customer.id,
      label: customer.code,
    }));
  },
  createCustomer: async (data: {
    name: string;
    code: string;
    isActive: boolean;
  }): Promise<AxiosResponse<Customer>> => {
    return api.post('/customers', data);
  },
  updateCustomer: async (
    id: number,
    data: {
      name?: string;
      code?: string;
      isActive?: boolean;
    },
  ): Promise<AxiosResponse<Customer>> => {
    return api.put(`/customers/${id}`, data);
  },
};
